import { PageHeader } from '@/client/components/common/PageHeader';
import { Button } from '@/client/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import { supabase } from '@/client/lib/supabase';
import { trpc } from '@/client/utils/trpc';
import { createFileRoute } from '@tanstack/react-router';
import { Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export const Route = createFileRoute('/_app/settings')({
  component: SettingsPage,
});

function SettingsPage() {
  return (
    <div className="mx-auto w-full min-w-0 max-w-3xl space-y-6">
      <PageHeader
        title="Settings"
        eyebrow="Account"
        description="Manage your profile and security."
        icon={Settings}
      />
      <ProfileCard />
      <SecurityCard />
    </div>
  );
}

function ProfileCard() {
  const utils = trpc.useUtils();
  const { data: me } = trpc.user.me.useQuery();
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const initialized = useRef(false);

  useEffect(() => {
    if (me?.full_name && !initialized.current) {
      setName(me.full_name);
      initialized.current = true;
    }
  }, [me]);

  const updateName = trpc.user.updateName.useMutation({
    onSuccess: () => {
      utils.user.me.invalidate();
      setStatus('success');
    },
    onError: (err) => {
      setStatus('error');
      setErrorMsg(err.message);
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('idle');
    updateName.mutate({ name });
  }

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Profile</CardTitle>
        <CardDescription>Update your display name.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="full-name">Full name</Label>
            <Input
              id="full-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your full name"
            />
          </div>
          <Button type="submit" disabled={updateName.isPending}>
            {updateName.isPending ? 'Saving…' : 'Save'}
          </Button>
          {status === 'success' && <p className="text-sm text-good">Name updated.</p>}
          {status === 'error' && <p className="text-sm text-destructive">{errorMsg}</p>}
        </form>
      </CardContent>
    </Card>
  );
}

function SecurityCard() {
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setStatus('idle');

    if (!next || !confirm) {
      setStatus('error');
      setErrorMsg('All fields are required.');
      return;
    }

    if (next !== confirm) {
      setStatus('error');
      setErrorMsg('New passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: next });

      if (updateError) {
        setStatus('error');
        setErrorMsg(updateError.message);
        return;
      }

      setNext('');
      setConfirm('');
      setStatus('success');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Security</CardTitle>
        <CardDescription>Change your password.</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="new-password">New password</Label>
            <Input
              id="new-password"
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm new password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
          <Button type="submit" disabled={loading}>
            {loading ? 'Updating…' : 'Update password'}
          </Button>
          {status === 'success' && <p className="text-sm text-good">Password updated.</p>}
          {status === 'error' && <p className="text-sm text-destructive">{errorMsg}</p>}
        </form>
      </CardContent>
    </Card>
  );
}
